import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectCompanyInfo } from "store/companySlice";
import { addToast } from "store/toastsSlice";

import Api from "api/Api";
import Button from "layout/atoms/Button";
import ContentBox from "layout/atoms/ContentBox";
import GridBoard from "layout/atoms/GridBoard";
import InputText from "layout/atoms/InputText";
import Label from "layout/atoms/Label";
import Loader from "layout/atoms/Loader";
import Modal from "layout/atoms/Modal";
import ToggleSwitch from "layout/atoms/ToggleSwitch";
import PersonList from "layout/molecules/PersonList";

import { UserTableHeader } from "../UsersListStyles";
import {
  AddMemberForm,
  AddMemberHint,
  AddMemberToggle,
  AppAccessList,
  AppAccessRow,
  ConfirmText,
  MemberActions,
  MemberStatusToggle,
} from "./CompanyMembersStyles";

const roleLabelStatus = {
  PRIMARY_OWNER: "COMPLETE",
  OWNER: "OPEN",
  MEMBER: "REVIEW",
};

function CompanyMembers() {
  const dispatch = useDispatch();
  const companyInfo = useSelector(selectCompanyInfo);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [changingStatusOf, setChangingStatusOf] = useState(null);
  const [memberToDeactivate, setMemberToDeactivate] = useState(null);

  const [memberToManageApps, setMemberToManageApps] = useState(null);
  const [selectedApps, setSelectedApps] = useState([]);
  const [isSavingApps, setIsSavingApps] = useState(false);

  const openAppAccess = (member) => {
    setSelectedApps(member.apps || []);
    setMemberToManageApps(member);
  };

  const toggleApp = (app, enabled) => {
    setSelectedApps((current) =>
      enabled ? [...current, app] : current.filter((a) => a !== app),
    );
  };

  const handleSaveApps = () => {
    setIsSavingApps(true);
    new Api()
      .changeCompanyMemberApps(
        companyInfo.id,
        memberToManageApps.userId,
        selectedApps,
      )
      .then((updated) => {
        setMembers((current) =>
          current.map((m) => (m.userId === updated.userId ? updated : m)),
        );
        setMemberToManageApps(null);
      })
      .catch(() => {
        dispatch(
          addToast({
            title: "toasts.error.title",
            description: "toasts.error.description",
            type: "error",
          }),
        );
      })
      .finally(() => setIsSavingApps(false));
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEmail("");
  };

  useEffect(() => {
    if (!companyInfo.id) {
      return;
    }
    setIsLoading(true);
    new Api()
      .getCompanyMembers(companyInfo.id)
      .then(setMembers)
      .catch(() => {
        dispatch(
          addToast({
            title: "toasts.error.title",
            description: "toasts.error.description",
            type: "error",
          }),
        );
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, companyInfo.id]);

  const handleAddMember = (e) => {
    e.preventDefault();
    setIsAdding(true);
    new Api()
      .addCompanyMember(companyInfo.id, { email: email.trim() })
      .then((member) => {
        setMembers((current) => [...current, member]);
        closeForm();
        dispatch(
          addToast({
            title: "toasts.memberAdded.title",
            description: "toasts.memberAdded.description",
            type: "success",
          }),
        );
      })
      .catch((error) => {
        const isRejected = error.message.startsWith("HTTP 400");
        dispatch(
          addToast({
            title: "toasts.error.title",
            description: isRejected
              ? "toasts.memberNotAdded.description"
              : "toasts.error.description",
            type: "error",
          }),
        );
      })
      .finally(() => setIsAdding(false));
  };

  const handleChangeStatus = (member) => {
    const api = new Api();
    const request = member.active
      ? api.deactivateCompanyMember(companyInfo.id, member.userId)
      : api.activateCompanyMember(companyInfo.id, member.userId);

    setChangingStatusOf(member.userId);
    request
      .then((updated) => {
        setMembers((current) =>
          current.map((m) => (m.userId === updated.userId ? updated : m)),
        );
      })
      .catch(() => {
        dispatch(
          addToast({
            title: "toasts.error.title",
            description: "toasts.error.description",
            type: "error",
          }),
        );
      })
      .finally(() => {
        setChangingStatusOf(null);
        setMemberToDeactivate(null);
      });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <GridBoard>
      <ContentBox>
        <UserTableHeader>
          <div className="avatar-col"></div>
          <div className="name-col">
            {t("settings.company.members.table.name")}
          </div>
          <div className="type-col">
            {t("settings.company.members.table.role")}
          </div>
        </UserTableHeader>
        <PersonList
          items={members.map((member) => ({
            id: member.userId,
            name: member.displayName || member.email,
            subtitle: member.email,
            avatarUrl: member.photoUrl || "/assets/illustrations/avatar.png",
            badge: (
              <MemberActions>
                {member.role !== "PRIMARY_OWNER" && (
                  <MemberStatusToggle
                    type="button"
                    onClick={() => openAppAccess(member)}
                  >
                    {t("settings.company.members.appAccess.open")}
                  </MemberStatusToggle>
                )}
                {member.role !== "PRIMARY_OWNER" && (
                  <MemberStatusToggle
                    type="button"
                    disabled={changingStatusOf === member.userId}
                    onClick={() =>
                      member.active
                        ? setMemberToDeactivate(member)
                        : handleChangeStatus(member)
                    }
                  >
                    {t(
                      member.active
                        ? "settings.company.members.deactivate"
                        : "settings.company.members.activate",
                    )}
                  </MemberStatusToggle>
                )}
                {!member.active && (
                  <Label status="DECLINED">
                    {t("settings.company.members.deactivated")}
                  </Label>
                )}
                <Label status={roleLabelStatus[member.role] || "REVIEW"}>
                  {t(`settings.company.members.roles.${member.role}`)}
                </Label>
              </MemberActions>
            ),
          }))}
        />
        {isFormOpen ? (
          <>
            <AddMemberForm onSubmit={handleAddMember}>
              <InputText
                type="email"
                name="email"
                placeholder={t("settings.company.members.add.placeholder")}
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                type="submit"
                size="lg"
                color="primary"
                disabled={isAdding || !email.includes("@")}
              >
                {t("settings.company.members.add.submit")}
              </Button>
              <Button size="lg" color="white" onClick={closeForm}>
                {t("buttons.cancel")}
              </Button>
            </AddMemberForm>
            <AddMemberHint>
              {t("settings.company.members.add.description")}
            </AddMemberHint>
          </>
        ) : (
          <AddMemberToggle type="button" onClick={() => setIsFormOpen(true)}>
            {t("settings.company.members.add.open")}
          </AddMemberToggle>
        )}
      </ContentBox>
      {memberToDeactivate && (
        <Modal
          open
          locked={changingStatusOf !== null}
          onClose={() => setMemberToDeactivate(null)}
        >
          <ContentBox
            header={
              <h3>{t("settings.company.members.deactivateModal.title")}</h3>
            }
            footer={
              <>
                <div />
                <div>
                  <Button
                    size="lg"
                    color="white"
                    disabled={changingStatusOf !== null}
                    onClick={() => setMemberToDeactivate(null)}
                  >
                    {t("buttons.cancel")}
                  </Button>
                  <Button
                    size="lg"
                    color="primary"
                    loading={changingStatusOf !== null}
                    onClick={() => handleChangeStatus(memberToDeactivate)}
                  >
                    {t("settings.company.members.deactivate")}
                  </Button>
                </div>
              </>
            }
          >
            <ConfirmText>
              <p>
                {t("settings.company.members.deactivateModal.aboutTo")}{" "}
                <strong>
                  {memberToDeactivate.email || memberToDeactivate.displayName}
                </strong>
                .
              </p>
              <p>{t("settings.company.members.deactivateModal.description")}</p>
            </ConfirmText>
          </ContentBox>
        </Modal>
      )}
      {memberToManageApps && (
        <Modal
          open
          locked={isSavingApps}
          onClose={() => setMemberToManageApps(null)}
        >
          <ContentBox
            header={<h3>{t("settings.company.members.appAccess.title")}</h3>}
            footer={
              <>
                <div />
                <div>
                  <Button
                    size="lg"
                    color="white"
                    disabled={isSavingApps}
                    onClick={() => setMemberToManageApps(null)}
                  >
                    {t("buttons.cancel")}
                  </Button>
                  <Button
                    size="lg"
                    color="primary"
                    loading={isSavingApps}
                    onClick={handleSaveApps}
                  >
                    {t("buttons.save")}
                  </Button>
                </div>
              </>
            }
          >
            <ConfirmText>
              <p>
                {t("settings.company.members.appAccess.description")}{" "}
                <strong>
                  {memberToManageApps.email || memberToManageApps.displayName}
                </strong>
                .
              </p>
            </ConfirmText>
            <AppAccessList>
              {(companyInfo.apps || []).map((app) => (
                <AppAccessRow key={app}>
                  <span>
                    {t(`settings.company.members.appAccess.apps.${app}`)}
                  </span>
                  <ToggleSwitch
                    id={`app-access-${app}`}
                    name={`app-access-${app}`}
                    checked={selectedApps.includes(app)}
                    disabled={isSavingApps}
                    onChange={(enabled) => toggleApp(app, enabled)}
                  />
                </AppAccessRow>
              ))}
            </AppAccessList>
          </ContentBox>
        </Modal>
      )}
    </GridBoard>
  );
}

export default CompanyMembers;
